import { fetchWonPrizes, type WonPrize } from "@/domain/marketing/api"
import { atom, useAtomValue, useStore } from "jotai"
import { useEffect } from "react"

/** The session customer's won prizes as last read; `unknown` until a read succeeds. */
export type WonPrizesSnapshot = { status: "unknown" } | { status: "ready"; prizes: WonPrize[] }

/** Server state shared by the game, the cart, checkout and Minha conta; never persisted. */
export const wonPrizesAtom = atom<WonPrizesSnapshot>({ status: "unknown" })

type Store = ReturnType<typeof useStore>

let inFlight: Promise<WonPrize[] | null> | null = null

/** Reads the won prizes again (one request at a time) and shares a successful answer; resolves to it, or null when the read fails. */
export function reloadWonPrizes(store: Store): Promise<WonPrize[] | null> {
  if (!inFlight) {
    inFlight = fetchWonPrizes()
      .then((prizes) => {
        if (prizes) store.set(wonPrizesAtom, { status: "ready", prizes })
        return prizes
      })
      .finally(() => {
        inFlight = null
      })
  }
  return inFlight
}

/** The won prizes, read when `enabled` turns on and nothing was read yet. */
export function useWonPrizes(enabled: boolean): WonPrizesSnapshot {
  const store = useStore()
  const snapshot = useAtomValue(wonPrizesAtom)
  const needed = enabled && snapshot.status === "unknown"

  useEffect(() => {
    if (needed) void reloadWonPrizes(store)
  }, [needed, store])

  return snapshot
}
